import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Input, InputType, CreateInputRequest, UpdateInputRequest } from '../../services/inputsApi';

// State interfaces
export interface InputsState {
  // Data
  inputs: Input[];
  selectedInput: Input | null;
  inputTypes: InputType[];
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  isEditing: boolean;
  isDialogOpen: boolean;
  validationErrors: string[];
  
  // List State
  searchTerm: string;
  filters: {
    active: string; // 'all', 'true', 'false'
    inputTypeId: number | null;
  };
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedInputIds: number[];
  bulkLoading: boolean;
}

const initialState: InputsState = {
  inputs: [],
  selectedInput: null,
  inputTypes: [],
  loading: false,
  error: null,
  isEditing: false,
  isDialogOpen: false,
  validationErrors: [],
  searchTerm: '',
  filters: {
    active: 'all',
    inputTypeId: null,
  },
  pagination: {
    page: 1,
    pageSize: 25,
    total: 0,
  },
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  selectedInputIds: [],
  bulkLoading: false,
};

const inputsSlice = createSlice({
  name: 'inputs',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setBulkLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data actions
    setInputs: (state, action: PayloadAction<Input[]>) => {
      state.inputs = action.payload;
      state.pagination.total = action.payload.length;
    },
    setSelectedInput: (state, action: PayloadAction<Input | null>) => {
      state.selectedInput = action.payload;
    },
    setInputTypes: (state, action: PayloadAction<InputType[]>) => {
      state.inputTypes = action.payload;
    },
    addInput: (state, action: PayloadAction<Input>) => {
      state.inputs.push(action.payload);
      state.pagination.total += 1;
    },
    updateInput: (state, action: PayloadAction<Input>) => {
      const index = state.inputs.findIndex(input => input.Id === action.payload.Id);
      if (index !== -1) {
        state.inputs[index] = action.payload;
      }
    },
    removeInput: (state, action: PayloadAction<number>) => {
      state.inputs = state.inputs.filter(input => input.Id !== action.payload);
      state.pagination.total -= 1;
    },
    
    // Dialog and form actions
    setDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isDialogOpen = action.payload;
      if (!action.payload) {
        state.selectedInput = null;
        state.isEditing = false;
        state.validationErrors = [];
      }
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    
    // Search and filters
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.pagination.page = 1; // Reset to first page when searching
    },
    setFilters: (state, action: PayloadAction<Partial<InputsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filtering
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<InputsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<InputsState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedInputIds: (state, action: PayloadAction<number[]>) => {
      state.selectedInputIds = action.payload;
    },
    toggleInputSelection: (state, action: PayloadAction<number>) => {
      const inputId = action.payload;
      const isSelected = state.selectedInputIds.includes(inputId);
      if (isSelected) {
        state.selectedInputIds = state.selectedInputIds.filter(id => id !== inputId);
      } else {
        state.selectedInputIds.push(inputId);
      }
    },
    selectAllInputs: (state) => {
      state.selectedInputIds = state.inputs.map(input => input.Id);
    },
    deselectAllInputs: (state) => {
      state.selectedInputIds = [];
    },
    
    // Reset state
    resetState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setBulkLoading,
  setError,
  setInputs,
  setSelectedInput,
  setInputTypes,
  addInput,
  updateInput,
  removeInput,
  setDialogOpen,
  setEditing,
  setValidationErrors,
  setSearchTerm,
  setFilters,
  setPagination,
  setStatistics,
  setSelectedInputIds,
  toggleInputSelection,
  selectAllInputs,
  deselectAllInputs,
  resetState,
} = inputsSlice.actions;

// Saga action types
export const FETCH_INPUTS = 'inputs/fetchInputs';
export const FETCH_INPUT_BY_ID = 'inputs/fetchInputById';
export const CREATE_INPUT = 'inputs/createInput';
export const UPDATE_INPUT = 'inputs/updateInput';
export const DELETE_INPUT = 'inputs/deleteInput';
export const FETCH_INPUT_TYPES = 'inputs/fetchInputTypes';
export const BULK_ACTIVATE_INPUTS = 'inputs/bulkActivateInputs';
export const BULK_DEACTIVATE_INPUTS = 'inputs/bulkDeactivateInputs';
export const FETCH_INPUTS_STATISTICS = 'inputs/fetchInputsStatistics';

// Saga action creators
export const fetchInputs = () => ({ type: FETCH_INPUTS });
export const fetchInputById = (id: number) => ({ type: FETCH_INPUT_BY_ID, payload: id });
export const createInput = (input: CreateInputRequest) => ({ type: CREATE_INPUT, payload: input });
export const updateInputAction = (input: UpdateInputRequest) => ({ type: UPDATE_INPUT, payload: input });
export const deleteInput = (id: number) => ({ type: DELETE_INPUT, payload: id });
export const fetchInputTypes = () => ({ type: FETCH_INPUT_TYPES });
export const bulkActivateInputs = (ids: number[]) => ({ type: BULK_ACTIVATE_INPUTS, payload: ids });
export const bulkDeactivateInputs = (ids: number[]) => ({ type: BULK_DEACTIVATE_INPUTS, payload: ids });
export const fetchInputsStatistics = () => ({ type: FETCH_INPUTS_STATISTICS });

export default inputsSlice.reducer; 